import React, { useMemo, useState } from 'react';
import {
  KatanaPathingSummaryDay,
  KatanaPathingSummaryQueryParams,
} from '@/types';
import { useGetKatanaPathingSummaryQuery } from '../../services/edgeApi';

type QueryMode = 'latest' | 'single' | 'range';

const formatNumber = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString() : '-';

const formatVolume = (value: number) =>
  Number.isFinite(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : '-';

const toPercent = (numerator: number, denominator: number): number => {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return 0;
  }
  return (numerator / denominator) * 100;
};

const buildQueryParams = (
  mode: QueryMode,
  dateInput: string,
  startDateInput: string,
  endDateInput: string,
): {
  params: KatanaPathingSummaryQueryParams;
  error: string | null;
} => {
  if (mode === 'latest') {
    return { params: {}, error: null };
  }

  if (mode === 'single') {
    if (!dateInput) {
      return {
        params: {},
        error: 'Select a date for single-day summary.',
      };
    }
    return {
      params: { date: dateInput },
      error: null,
    };
  }

  if (!startDateInput && !endDateInput) {
    return {
      params: {},
      error: 'Provide start date and/or end date for range summary.',
    };
  }

  if (startDateInput && endDateInput && endDateInput < startDateInput) {
    return {
      params: {},
      error: 'End date must be on or after start date.',
    };
  }

  return {
    params: {
      start_date: startDateInput || undefined,
      end_date: endDateInput || undefined,
    },
    error: null,
  };
};

export const PathingStatusTab: React.FC = () => {
  const [queryMode, setQueryMode] = useState<QueryMode>('latest');
  const [dateInput, setDateInput] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [appliedParams, setAppliedParams] = useState<KatanaPathingSummaryQueryParams>({});

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetKatanaPathingSummaryQuery(appliedParams);

  const summaries = useMemo<KatanaPathingSummaryDay[]>(() => {
    if (!data) return [];
    const rows = Array.isArray(data) ? data : [data];
    return [...rows].sort((a, b) => a.gas_date.localeCompare(b.gas_date));
  }, [data]);

  const latestSummary = summaries.length > 0 ? summaries[summaries.length - 1] : null;

  const apiErrorMessage = useMemo(() => {
    const err = error as any;
    if (!err) return null;

    if (typeof err?.data === 'string') return err.data;
    if (typeof err?.data?.error === 'string') return err.data.error;
    if (typeof err?.data?.detail === 'string') return err.data.detail;
    if (typeof err?.error === 'string') return err.error;

    return 'Request failed. Please verify date filters and try again.';
  }, [error]);

  const onApply = (evt: React.FormEvent) => {
    evt.preventDefault();
    const { params, error: nextError } = buildQueryParams(
      queryMode,
      dateInput,
      startDateInput,
      endDateInput,
    );

    if (nextError) {
      setClientError(nextError);
      return;
    }

    setClientError(null);
    setAppliedParams(params);
  };

  return (
    <div className="h-100 d-flex flex-column p-3 gap-3">
      <div className="card card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-1">Pathing Status</h5>
          </div>
        </div>

        <form onSubmit={onApply} className="d-flex flex-column gap-3 mb-3">
          <div>
            <div className="form-check form-check-inline">
              <input
                id="pathing-status-mode-latest"
                className="form-check-input"
                type="radio"
                name="pathing-status-mode"
                checked={queryMode === 'latest'}
                onChange={() => setQueryMode('latest')}
              />
              <label className="form-check-label" htmlFor="pathing-status-mode-latest">
                Latest
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                id="pathing-status-mode-single"
                className="form-check-input"
                type="radio"
                name="pathing-status-mode"
                checked={queryMode === 'single'}
                onChange={() => setQueryMode('single')}
              />
              <label className="form-check-label" htmlFor="pathing-status-mode-single">
                Single Day
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                id="pathing-status-mode-range"
                className="form-check-input"
                type="radio"
                name="pathing-status-mode"
                checked={queryMode === 'range'}
                onChange={() => setQueryMode('range')}
              />
              <label className="form-check-label" htmlFor="pathing-status-mode-range">
                Date Range
              </label>
            </div>
          </div>

          {queryMode === 'single' && (
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">Date</label>
                <input
                  className="form-control"
                  type="date"
                  value={dateInput}
                  onChange={(evt) => setDateInput(evt.target.value)}
                />
              </div>
            </div>
          )}

          {queryMode === 'range' && (
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">Start Date</label>
                <input
                  className="form-control"
                  type="date"
                  value={startDateInput}
                  onChange={(evt) => setStartDateInput(evt.target.value)}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">End Date</label>
                <input
                  className="form-control"
                  type="date"
                  value={endDateInput}
                  onChange={(evt) => setEndDateInput(evt.target.value)}
                />
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button className="btn btn-primary" type="submit" disabled={isLoading || isFetching}>
              Get Summary
            </button>
          </div>
        </form>

        {clientError && <div className="alert alert-danger mb-3">{clientError}</div>}
        {apiErrorMessage && <div className="alert alert-danger mb-3">{apiErrorMessage}</div>}

        {!isLoading && summaries.length === 0 && !clientError && !apiErrorMessage && (
          <div className="text-muted">No summary data found for the selected dates.</div>
        )}

        {latestSummary && (
          <div className="row g-2 mb-3">
            <div className="col-12 col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="text-muted small">Gas Date</div>
                <div className="fw-semibold">{latestSummary.gas_date}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="text-muted small">Total Paths</div>
                <div className="fw-semibold">{formatNumber(latestSummary.total_paths)}</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="text-muted small">Valid Paths</div>
                <div className="fw-semibold">
                  {formatNumber(latestSummary.valid_paths)} ({toPercent(
                    latestSummary.valid_paths,
                    latestSummary.total_paths,
                  ).toFixed(1)}%)
                </div>
              </div>
            </div>
          </div>
        )}

        {summaries.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>Gas Date</th>
                  <th>Total Paths</th>
                  <th>Valid Paths</th>
                  <th>Validity %</th>
                  <th>Receipt Volume (All)</th>
                  <th>Receipt Volume (Valid)</th>
                  <th>Delivery Volume (All)</th>
                  <th>Delivery Volume (Valid)</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((summary) => (
                  <tr key={summary.gas_date}>
                    <td>{summary.gas_date}</td>
                    <td>{formatNumber(summary.total_paths)}</td>
                    <td>{formatNumber(summary.valid_paths)}</td>
                    <td>{toPercent(summary.valid_paths, summary.total_paths).toFixed(1)}%</td>
                    <td>{formatVolume(summary.receipt_trade.volume_all)}</td>
                    <td>{formatVolume(summary.receipt_trade.volume_valid)}</td>
                    <td>{formatVolume(summary.delivery_trade.volume_all)}</td>
                    <td>{formatVolume(summary.delivery_trade.volume_valid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
